import { useListItem } from "@floating-ui/react";
import classNames from "classnames";
import React from "react";

import { PolymorphicComponentProps } from "lib/@types/utils/polymorphic.types";

import { useDropdownContext } from "./dropdown.component";

type DropdownItemProps<T extends React.ElementType> = PolymorphicComponentProps<
    T,
    {
        label: string | number;
        onClick?: () => void;
        disabled?: boolean;
    },
    "a" | "button"
>;

const DropdownItem = <T extends React.ElementType = "button">({
    label,
    onClick,
    className,
    disabled,
    tag,
    children,
    ...attrs
}: DropdownItemProps<T>) => {
    const Tag = tag || "button";
    const { activeIndex, selectedIndex, getItemProps, handleSelect } =
        useDropdownContext();

    const { ref, index } = useListItem({ label: label.toString() });

    const isActive = activeIndex === index;
    const isSelected = selectedIndex === index;

    const classes = classNames(className, `dropdown-item`, {
        disabled: disabled,
    });

    return (
        <Tag
            ref={ref}
            role="option"
            aria-selected={isActive && isSelected}
            tabIndex={isActive ? 0 : -1}
            {...getItemProps({
                onClick: () => {
                    handleSelect(index);
                    if (onClick) {
                        setTimeout(() => onClick());
                    }
                },
            })}
            className={classes}
            disabled={disabled}
            {...attrs}
        >
            {children || label}
        </Tag>
    );
};

export { DropdownItem };
